import type { GameState } from '../state';
import { D, type Decimal } from '../math/num';
import type { ActiveEnemy } from './spawn';
import { OVERLORD_ENEMY_ID } from './overlords';

/**
 * Enrage — the stalemate breaker (P38). Healing made the frame nearly
 * unkillable: if you could out-sustain a wave you could face-tank it forever,
 * even when your DPS could never clear it. Rather than nerf healing, every
 * enemy gets ANGRIER the longer the current encounter drags: damage ramps per
 * nominal attack, linearly, until either side falls.
 *
 * One "attack" = ENRAGE_ATTACK_MS of fight time (matches the visible swing
 * cadence in the arena). Stacks reset whenever a fresh encounter starts (new
 * wave horde, overlord duel, post-defeat respawn). Linear on purpose: a fight
 * you can win inside the expected window barely notices it; a fight you cannot
 * win becomes lethal in minutes.
 */

export const ENRAGE_ATTACK_MS = 2_000;
export const ENRAGE_PER_ATTACK_NORMAL = 0.01;
export const ENRAGE_PER_ATTACK_GUARDIAN = 0.02;
export const ENRAGE_PER_ATTACK_OVERLORD = 0.03;

/** Whole attacks the current encounter has endured. */
export function enrageStacks(state: GameState): number {
  return Math.floor(state.combat.enrageMs / ENRAGE_ATTACK_MS);
}

/** Per-attack damage growth for an enemy kind (overlord > guardian > mob). */
export function enrageRatePerAttack(e: ActiveEnemy): number {
  if (e.id === OVERLORD_ENEMY_ID) return ENRAGE_PER_ATTACK_OVERLORD;
  return e.isBoss ? ENRAGE_PER_ATTACK_GUARDIAN : ENRAGE_PER_ATTACK_NORMAL;
}

/** The damage multiplier an enemy currently swings with. */
export function enrageMult(state: GameState, e: ActiveEnemy): Decimal {
  return D(1 + enrageRatePerAttack(e) * enrageStacks(state));
}

/** Display bonus (fraction, e.g. 0.35 = +35%) for the angriest thing on the
 *  field — what the arena chip and the renderer's red ramp show. */
export function enrageBonus(state: GameState): number {
  const stacks = enrageStacks(state);
  if (stacks <= 0 || state.combat.enemies.length === 0) return 0;
  let rate = 0;
  for (const e of state.combat.enemies) rate = Math.max(rate, enrageRatePerAttack(e));
  return rate * stacks;
}
